import { RotateCw } from 'lucide-react';
import { Link, useRouteError } from 'react-router-dom';

import { Button } from '../shared/ui/Button';

import { useAuth } from './AuthProvider';
import s from './RouteErrorBoundary.module.css';

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An unexpected error occurred.';
}

// A failed dynamic import (a stale chunk after a deploy) reads very differently
// to the user than a genuine crash, so the copy adapts to which one happened.
function isChunkLoadError(message: string): boolean {
  return /dynamically imported module|failed to fetch|module script failed|error loading/i.test(
    message,
  );
}

/** Route-level error boundary (router `errorElement`) for any failed render — most
 *  often a lazy chunk that couldn't load after a new deploy. */
export function RouteErrorBoundary(): JSX.Element {
  const error = useRouteError();
  const { user } = useAuth();
  const message = errorMessage(error);
  const staleChunk = isChunkLoadError(message);
  // "/" is the marketing page. Sending a signed-in user there after a crash
  // puts "Log in" and "Get started" in front of them, which reads as having
  // been logged out — the same lie NotFoundPage was fixed for. /app routes to
  // whichever workspace their role owns.
  const signedIn = user !== null;

  return (
    <div className={s.shell}>
      <div className={s.card} role="alert">
        <span className={s.badge} aria-hidden="true">
          <RotateCw size={20} />
        </span>
        <h1 className={s.title}>
          {staleChunk ? 'A new version is available' : 'Something went wrong'}
        </h1>
        <p className={s.body}>
          {staleChunk
            ? 'This page couldn’t load — the app was likely updated while your tab was open. Reloading fetches the latest version.'
            : 'This page ran into an unexpected error. Reloading usually fixes it; if it keeps happening, let your administrator know.'}
        </p>
        <div className={s.actions}>
          <Button onClick={() => window.location.reload()}>
            <RotateCw size={16} aria-hidden="true" />
            Reload page
          </Button>
          <Link to={signedIn ? '/app' : '/'} className={s.homeLink}>
            {signedIn ? 'Back to my workspace' : 'Back to home'}
          </Link>
        </div>
        {import.meta.env.DEV ? (
          <details className={s.details}>
            <summary className={s.summary}>Technical details</summary>
            <pre className={s.pre}>{message}</pre>
          </details>
        ) : null}
      </div>
    </div>
  );
}
